import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, Package, FileText, Settings, LogOut, 
  Menu, X, ArrowLeft, Upload, Save, Image
} from 'lucide-react';
import peptLogo from '../../assets/pept-logo-cropped.png';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState({
    hero_image: '/images/hero-lab.png',
    hero_opacity: 0.3,
    whatsapp_number: '+595 986 586920',
    support_email: 'support@pep-t.com'
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        setMessage('Settings saved successfully!');
      }
    } catch (err) {
      setMessage('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setMessage('');
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/api/admin/upload/hero`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setSettings({ ...settings, hero_image: data.path + '?t=' + Date.now() });
        setMessage('Hero image uploaded successfully!');
      }
    } catch (err) {
      setMessage('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Peptides', icon: Package, path: '/admin/peptides' },
    { label: 'COA Library', icon: FileText, path: '/admin/coas' },
    { label: 'Site Settings', icon: Settings, path: '/admin/settings', active: true },
  ];

  return (
    <div className="min-h-screen bg-neutral-900 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-neutral-800 border-r border-neutral-700 transform transition-transform lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b border-neutral-700">
          <img src={peptLogo} alt="PEP-T.COM" className="h-10" />
          <p className="text-neutral-400 text-xs mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                item.active 
                  ? 'bg-red-600 text-white' 
                  : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-neutral-400 hover:text-red-400 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="bg-neutral-800 border-b border-neutral-700 px-4 py-4 flex items-center justify-between lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-neutral-400 hover:text-white lg:hidden"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Site Settings
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-500 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </header>

        {/* Settings content */}
        <div className="p-4 lg:p-6 space-y-6">
          {message && (
            <div className={`p-4 rounded-sm ${message.includes('Error') ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'}`}>
              {message}
            </div>
          )}

          {/* Hero Image */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-purple-400" />
              Hero Image
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <p className="text-neutral-400 text-sm mb-4">
                  This is the background image shown on the homepage hero section.
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleHeroUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-neutral-700 text-white rounded-sm hover:bg-neutral-600 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload New Image'}
                </button>

                <div className="mt-4">
                  <label className="block text-sm text-neutral-400 mb-2">Image Opacity</label>
                  <input
                    type="range"
                    min="0.1"
                    max="0.5"
                    step="0.05"
                    value={settings.hero_opacity}
                    onChange={(e) => setSettings({ ...settings, hero_opacity: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-neutral-500 text-xs mt-1">{Math.round(settings.hero_opacity * 100)}%</p>
                </div>
              </div>
              
              <div className="bg-neutral-900 rounded-sm overflow-hidden">
                <img 
                  src={settings.hero_image} 
                  alt="Hero Preview" 
                  className="w-full h-48 object-cover"
                  style={{ opacity: settings.hero_opacity }}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Contact Information</h2>
            
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-neutral-400 mb-2">WhatsApp Number</label>
                <input
                  type="text"
                  value={settings.whatsapp_number}
                  onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-sm text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Support Email</label>
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-sm text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
